import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button, Form, Row, Col, Badge, Card, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import httpService from 'services/httpService';
import {
  MdAdd,
  MdCloudUpload,
  MdCheckCircle,
  MdErrorOutline,
  MdImage,
  MdInsertDriveFile,
  MdMovie,
  MdDelete,
  MdOpenInNew,
  MdAttachFile,
  MdHelpOutline
} from 'react-icons/md';
import { BsLightningFill, BsExclamationTriangle, BsShieldCheck } from 'react-icons/bs';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';
import { formatContextText } from '../Common/formatContextText';

const CreateIssueModal = ({ show, onHide, onSubmit, courseLabs = [], courseBookGroups = [], defaultContext = null }) => {
  const navigate = useNavigate();
  const params = useParams();
  const userInfo = useSelector(state => state?.userInfo) || {};
  const userMode = userInfo?.mode;
  const token = userInfo?.token || localStorage.getItem('token');
  const routeCourseId =
    params?.courseId ||
    defaultContext?.courseId ||
    defaultContext?.course ||
    (window.location.pathname.match(/\/course\/(\d+)/)?.[1]) ||
    (window.location.pathname.match(/\/courseware\/course\/(\d+)/)?.[1]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [category, setCategory] = useState(''); // Default unselected

  // Hierarchy contexts
  const [labs, setLabs] = useState(courseLabs || []);
  const [bookGroups, setBookGroups] = useState(courseBookGroups || []);
  const [loadingContexts, setLoadingContexts] = useState(false);

  // Hierarchy selections
  const [labId, setLabId] = useState('');
  const [activityName, setActivityName] = useState('');
  const [bookGroupId, setBookGroupId] = useState('');
  const [bookConceptTitle, setBookConceptTitle] = useState('');

  // File Upload State & Preview
  const [mediaFile, setMediaFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Validation & Touched States
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [createdIssueSummary, setCreatedIssueSummary] = useState(null);

  const [useFetchedContext, setUseFetchedContext] = useState(!!defaultContext);

  // Sync props if provided externally
  useEffect(() => {
    if (courseLabs && courseLabs.length > 0) {
      setLabs(courseLabs);
    }
  }, [courseLabs]);

  useEffect(() => {
    if (courseBookGroups && courseBookGroups.length > 0) {
      setBookGroups(courseBookGroups);
    }
  }, [courseBookGroups]);

  // Self-fetch contexts if empty when modal is shown
  useEffect(() => {
    if (!show || !routeCourseId) return;

    // If both are already loaded, no need to re-fetch
    if (labs.length > 0 && bookGroups.length > 0) return;

    let isMounted = true;
    const fetchContexts = async () => {
      setLoadingContexts(true);
      try {
        const rawToken = token || localStorage.getItem('token');
        const authHeader = rawToken ? { Authorization: rawToken.startsWith('Token ') ? rawToken : `Token ${rawToken}` } : {};
        const response = await httpService.get(`/issues/getCourseContexts/${routeCourseId}`, {
          headers: authHeader
        });
        if (isMounted && response?.data) {
          if (response.data.labs) setLabs(response.data.labs);
          if (response.data.book_groups) setBookGroups(response.data.book_groups);
        }
      } catch (err) {
        console.error('Error fetching course contexts in CreateIssueModal:', err);
      } finally {
        if (isMounted) setLoadingContexts(false);
      }
    };

    fetchContexts();
    return () => { isMounted = false; };
  }, [show, routeCourseId, token]);

  // Clean and format context text to avoid misleading tags (e.g. "Programming Labs" or "(Quiz)")
  // and resolve accurate lab title (e.g. "lab_1" instead of "Lab #5")
  const getCleanContextText = (ctx) => {
    if (!ctx) return '';

    // 1. Resolve accurate lab title from labs list if available
    let resolvedLabTitle = ctx.labTitle;
    if ((!resolvedLabTitle || resolvedLabTitle.startsWith('Lab #') || resolvedLabTitle.startsWith('Lab:')) && ctx.labId && labs && labs.length > 0) {
      const foundLab = labs.find(l => String(l.id) === String(ctx.labId));
      if (foundLab && foundLab.title) {
        resolvedLabTitle = foundLab.title;
      }
    }

    let text = ctx.contextText || ctx.context_text || '';

    // If text contains "Lab #<id>", replace it with the resolved lab title
    if (resolvedLabTitle && ctx.labId) {
      text = text.replace(new RegExp(`Lab\\s*#${ctx.labId}\\b`, 'i'), resolvedLabTitle);
    }

    if (!text) {
      const act = (ctx.activityName || '').replace(/\s*\(Quiz\)/gi, '').trim();
      if (resolvedLabTitle) {
        text = act ? `Labs > ${resolvedLabTitle} > ${act}` : `Labs > ${resolvedLabTitle}`;
      } else {
        text = act ? `Labs > Lab ${ctx.labId} > ${act}` : `Lab: ${ctx.labId || ''}`;
      }
    }

    return formatContextText(text);
  };

  // Ensure default labId & activityName when labs load or category changes
  useEffect(() => {
    if (!useFetchedContext && (category === 'LAB' || category === 'LAB_ACTIVITY' || category === 'QUIZ_ACTIVITY')) {
      if (labs && labs.length > 0) {
        let currentLab = labs.find(l => String(l.id) === String(labId));
        if (!currentLab) {
          currentLab = labs[0];
          setLabId(String(currentLab.id));
        }

        if (category === 'LAB_ACTIVITY') {
          const acts = (currentLab?.programming_activities && currentLab.programming_activities.length > 0)
            ? currentLab.programming_activities
            : (currentLab?.activities || []).filter(a => a.type !== 'other');
          const fallbackActs = acts.length > 0 ? acts : (currentLab?.activities || []);
          if (fallbackActs.length > 0) {
            const hasValidAct = fallbackActs.some(a => (a.name || a.title) === activityName);
            if (!hasValidAct) {
              setActivityName(fallbackActs[0].name || fallbackActs[0].title);
            }
          } else {
            setActivityName('');
          }
        } else if (category === 'QUIZ_ACTIVITY') {
          const quizActs = (currentLab?.quiz_activities && currentLab.quiz_activities.length > 0)
            ? currentLab.quiz_activities
            : (currentLab?.activities || []).filter(a => a.type === 'other' || (a.name && a.name.includes('(Quiz)')));
          if (quizActs.length > 0) {
            const hasValidQuiz = quizActs.some(a => {
              const aName = (a.raw_name || a.name || a.title || '').replace(/\s*\(Quiz\)\s*$/i, '');
              return aName === activityName;
            });
            if (!hasValidQuiz) {
              const firstClean = (quizActs[0].raw_name || quizActs[0].name || quizActs[0].title || '').replace(/\s*\(Quiz\)\s*$/i, '');
              setActivityName(firstClean);
            }
          } else {
            setActivityName('');
          }
        }
      }
    }
  }, [category, labs, labId, useFetchedContext, activityName]);

  // Ensure default bookGroupId & bookConceptTitle when bookGroups load or category changes
  useEffect(() => {
    if (!useFetchedContext && category === 'MULTIMEDIA_CONCEPT') {
      if (bookGroups && bookGroups.length > 0) {
        let currentGroup = bookGroups.find(g => String(g.id) === String(bookGroupId));
        if (!currentGroup) {
          currentGroup = bookGroups[0];
          setBookGroupId(String(currentGroup.id));
        }

        const concepts = currentGroup?.concepts || [];
        if (concepts.length > 0) {
          const hasValidConcept = concepts.some(c => (c.title || c.name) === bookConceptTitle);
          if (!hasValidConcept) {
            setBookConceptTitle(concepts[0].title || concepts[0].name);
          }
        } else {
          setBookConceptTitle('');
        }
      }
    }
  }, [category, bookGroups, bookGroupId, useFetchedContext, bookConceptTitle]);

  // Sync defaultContext if provided
  useEffect(() => {
    if (defaultContext) {
      setUseFetchedContext(true);
      if (defaultContext.category) setCategory(defaultContext.category);
      if (defaultContext.labId) setLabId(String(defaultContext.labId));
      if (defaultContext.activityName) setActivityName(defaultContext.activityName);
    } else {
      setUseFetchedContext(false);
    }
  }, [defaultContext, show]);

  // Clean up object URLs to avoid memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Reset modal state on close/open
  const handleModalClose = () => {
    setTitle('');
    setDescription('');
    setPriority('MEDIUM');
    setCategory('');
    setLabId('');
    setActivityName('');
    setBookGroupId('');
    setBookConceptTitle('');
    setMediaFile(null);
    setPreviewUrl(null);
    setErrors({});
    setIsSubmitted(false);
    setCreatedIssueSummary(null);
    onHide();
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};

    if (!useFetchedContext && !category) {
      newErrors.category = 'Please select an issue category.';
    }

    if (!title.trim()) {
      newErrors.title = 'Issue Title is required';
    } else if (title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters long';
    } else if (title.length > 100) {
      newErrors.title = 'Title cannot exceed 100 characters';
    }

    if (!description.trim()) {
      newErrors.description = 'Issue Description is required';
    } else if (description.trim().length < 15) {
      newErrors.description = 'Please provide a detailed description (at least 15 characters)';
    }

    if (mediaFile && mediaFile.size > 10 * 1024 * 1024) {
      newErrors.mediaFile = 'File size exceeds maximum allowed limit of 10MB';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Process File selection
  const processFile = (file) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, mediaFile: 'File size exceeds maximum limit of 10MB' }));
      displayGlobalMessage('File size exceeds maximum limit of 10MB', 'error');
      return;
    }

    setErrors(prev => ({ ...prev, mediaFile: null }));
    setMediaFile(file);

    if (file.type && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setMediaFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (file) => {
    if (file.type && file.type.startsWith('image/')) return <MdImage size={24} className="text-primary" />;
    if (file.type && file.type.startsWith('video/')) return <MdMovie size={24} className="text-danger" />;
    return <MdInsertDriveFile size={24} className="text-info" />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      displayGlobalMessage('Please fill all required fields correctly.', 'error');
      return;
    }

    let contextText = '';
    let finalCategory = category;

    if (useFetchedContext && defaultContext) {
      finalCategory = defaultContext.category || category || 'LAB';
      contextText = getCleanContextText(defaultContext);
    } else {
      // Build hierarchical context string based on category choice
      const selectedLabObj = labs.find(l => String(l.id) === String(labId));
      const labTitleDisplay = selectedLabObj ? selectedLabObj.title : (labId ? `Lab ${labId}` : 'General Lab');

      const selectedBookGroupObj = bookGroups.find(g => String(g.id) === String(bookGroupId));
      const bookGroupTitleDisplay = selectedBookGroupObj ? selectedBookGroupObj.title : (bookGroupId ? `Chapter ${bookGroupId}` : 'General Topic');

      const cleanAct = (activityName || '').replace(/\s*\(Quiz\)\s*$/i, '');

      if (category === 'LAB') {
        contextText = `Labs > ${labTitleDisplay}`;
      } else if (category === 'LAB_ACTIVITY') {
        contextText = cleanAct
          ? `Labs > ${labTitleDisplay} > ${cleanAct}`
          : `Labs > ${labTitleDisplay}`;
      } else if (category === 'QUIZ_ACTIVITY') {
        contextText = cleanAct
          ? `Labs > ${labTitleDisplay} > ${cleanAct}`
          : `Labs > ${labTitleDisplay} > Quiz`;
      } else if (category === 'MULTIMEDIA_CONCEPT') {
        contextText = bookConceptTitle
          ? `Multimedia Book > ${bookGroupTitleDisplay} > ${bookConceptTitle}`
          : `Multimedia Book > ${bookGroupTitleDisplay}`;
      }
    }

    const effectiveLabId = useFetchedContext && defaultContext
      ? defaultContext.labId
      : (finalCategory === 'LAB' || finalCategory === 'LAB_ACTIVITY' || finalCategory === 'QUIZ_ACTIVITY' ? labId : null);

    let effectiveActivityId = useFetchedContext && defaultContext ? defaultContext.activityId : null;
    if (!effectiveActivityId && (finalCategory === 'LAB_ACTIVITY' || finalCategory === 'QUIZ_ACTIVITY')) {
      const selectedLab = (labs || []).find(l => String(l.id) === String(effectiveLabId));
      const allActs = selectedLab?.activities || [];
      const cleanAct = (activityName || '').replace(/\s*\(Quiz\)\s*$/i, '');
      const selectedAct = allActs.find(a => {
        const aName = (a.raw_name || a.name || a.title || '').replace(/\s*\(Quiz\)\s*$/i, '');
        return aName === cleanAct;
      });
      effectiveActivityId = selectedAct?.id || null;
    }

    let effectiveConceptId = useFetchedContext && defaultContext ? defaultContext.conceptId : null;
    if (!effectiveConceptId && finalCategory === 'MULTIMEDIA_CONCEPT') {
      const selectedGroup = (bookGroups || []).find(g => String(g.id) === String(bookGroupId));
      const selectedConc = selectedGroup?.concepts?.find(c => (c.name || c.title) === bookConceptTitle);
      effectiveConceptId = selectedConc?.id || null;
    }

    const newIssueData = {
      title: title.trim(),
      description: description.trim(),
      priority,
      category: finalCategory,
      context_text: formatContextText(contextText),
      labId: effectiveLabId,
      activityId: effectiveActivityId,
      conceptId: effectiveConceptId,
      mediaFile: mediaFile,
      mediaName: mediaFile ? mediaFile.name : null
    };

    try {
      let createdRes = null;
      if (onSubmit) {
        createdRes = await onSubmit(newIssueData);
      } else {
        const fd = new FormData();
        fd.append('course', routeCourseId);
        fd.append('title', newIssueData.title);
        fd.append('description', newIssueData.description);
        fd.append('priority', newIssueData.priority);
        fd.append('category', newIssueData.category);
        fd.append('context', (newIssueData.category === 'LAB_ACTIVITY' || newIssueData.category === 'QUIZ_ACTIVITY' || newIssueData.category === 'LAB') ? 'lab' : 'course');
        fd.append('context_text', newIssueData.context_text);
        if (newIssueData.labId) fd.append('lab_id', newIssueData.labId);
        if (newIssueData.activityId) fd.append('activity_id', newIssueData.activityId);
        if (newIssueData.conceptId) fd.append('concept_id', newIssueData.conceptId);
        if (newIssueData.mediaFile) fd.append('supporting_media', newIssueData.mediaFile);

        const rawToken = token || localStorage.getItem('token');
        const authHeaders = rawToken ? { Authorization: rawToken.startsWith('Token ') ? rawToken : `Token ${rawToken}` } : {};
        const res = await httpService.post('/issues/createIssue/', fd, { headers: authHeaders });
        createdRes = res.data?.issue || res.data;
      }

      const actualIssue = createdRes?.issue || (createdRes?.id ? createdRes : null) || {};
      const actualId = actualIssue.id || null;

      setCreatedIssueSummary({
        id: actualId,
        title: actualIssue.title || title.trim(),
        description: actualIssue.description || description.trim(),
        context: actualIssue.context_text || contextText,
        priority: actualIssue.priority || priority,
        priority_display: actualIssue.priority_display || (priority === 'HIGH' ? 'High' : priority === 'LOW' ? 'Low' : 'Medium'),
        status: actualIssue.status_display || actualIssue.status || 'Issue Unread',
        created_at: actualIssue.created_at ? new Date(actualIssue.created_at).toLocaleString() : new Date().toLocaleString(),
        mediaName: mediaFile ? mediaFile.name : null,
        mediaUrl: actualIssue.supporting_media_url || previewUrl
      });
      setIsSubmitted(true);
      displayGlobalMessage('Issue created successfully!', 'success');
    } catch (err) {
      const errMsg = err?.response?.data?.errorMsg || err?.message || 'Failed to create issue';
      displayGlobalMessage(errMsg, 'error');
    }
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Modal.Title style={{ fontSize: '1.2rem', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {isSubmitted ? (
            <>
              <MdCheckCircle style={{ color: '#16a34a' }} size={24} />
              Issue Submitted Successfully
            </>
          ) : (
            <>
              <MdAdd style={{ color: '#2563eb' }} size={24} />
              Create a New Academic Issue
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      {!isSubmitted ? (
        <Form onSubmit={handleSubmit} noValidate>
          <Modal.Body style={{ padding: '1.5rem 2rem', maxHeight: '75vh', overflowY: 'auto' }}>

            {/* Auto-Detected Context Card (if opened from a specific page/activity context) */}
            {defaultContext && (
              <Card
                className="mb-3 border-0 shadow-sm"
                style={{
                  background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
                  border: '1px solid #bfdbfe',
                  borderLeft: '4px solid #2563eb',
                  borderRadius: '10px'
                }}
              >
                <Card.Body className="py-3 px-3">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                      <span
                        className="fw-bold small text-uppercase d-inline-flex align-items-center gap-1 mb-1"
                        style={{ color: '#1d4ed8', fontSize: '0.72rem', letterSpacing: '0.05em' }}
                      >
                        Auto-Detected Page Context
                        {(defaultContext.category === 'QUIZ_ACTIVITY' || defaultContext.activityType === 'other') && (
                          <span className="badge bg-primary text-white ms-1" style={{ fontSize: '0.68rem', textTransform: 'none', fontWeight: 600 }}>
                            Quiz Activity
                          </span>
                        )}
                        {(defaultContext.category === 'LAB_ACTIVITY' || defaultContext.activityType === 'programming') && (
                          <span className="badge bg-secondary text-white ms-1" style={{ fontSize: '0.68rem', textTransform: 'none', fontWeight: 600 }}>
                            Programming Activity
                          </span>
                        )}
                      </span>
                      <div className="fw-bold small" style={{ color: '#1e3a8a', fontSize: '0.92rem' }}>
                        {getCleanContextText(defaultContext)}
                      </div>
                    </div>

                    <Form.Check
                      type="switch"
                      id="use-fetched-context-switch"
                      label={<span className="small fw-bold" style={{ color: '#1e293b', fontSize: '0.86rem' }}>Is this issue regarding this context?</span>}
                      checked={useFetchedContext}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setUseFetchedContext(checked);
                        if (!checked) {
                          setCategory('');
                        } else if (defaultContext) {
                          setCategory(defaultContext.category || 'LAB');
                          if (defaultContext.labId) setLabId(String(defaultContext.labId));
                          if (defaultContext.activityName) setActivityName(defaultContext.activityName);
                        }
                      }}
                    />
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Priority & Category Selection Row */}
            <Row className="mb-3 g-3">
              {!useFetchedContext && (
                <Col md={6}>
                  <Form.Label className="fw-semibold text-slate-700" style={{ fontSize: '0.92rem' }}>
                    Issue Category <span style={{ color: '#dc2626' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      if (errors.category) setErrors({ ...errors, category: null });
                    }}
                    isInvalid={!!errors.category}
                    style={{ borderRadius: '8px', fontSize: '0.9rem' }}
                  >
                    <option value="">-- Select Category --</option>
                    <option value="LAB">Lab</option>
                    <option value="LAB_ACTIVITY">Programming Activity</option>
                    <option value="QUIZ_ACTIVITY">Quiz Activity</option>
                    <option value="MULTIMEDIA_CONCEPT">Multimedia Book</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">{errors.category}</Form.Control.Feedback>
                </Col>
              )}

              <Col md={6}>
                <Form.Label className="fw-semibold text-slate-700" style={{ fontSize: '0.92rem', display: 'block' }}>
                  Priority Level <span style={{ color: '#dc2626' }}>*</span>
                </Form.Label>
                <div className="d-flex gap-2">
                  <Button
                    type="button"
                    variant={priority === 'LOW' ? 'info' : 'outline-secondary'}
                    size="sm"
                    className="flex-fill fw-bold d-flex align-items-center justify-content-center gap-1"
                    onClick={() => setPriority('LOW')}
                    style={{ borderRadius: '6px', padding: '0.4rem 0.5rem', fontSize: '0.85rem' }}
                  >
                    <BsShieldCheck size={13} />
                    Low
                  </Button>
                  <Button
                    type="button"
                    variant={priority === 'MEDIUM' ? 'warning' : 'outline-secondary'}
                    size="sm"
                    className="flex-fill fw-bold d-flex align-items-center justify-content-center gap-1"
                    onClick={() => setPriority('MEDIUM')}
                    style={{ borderRadius: '6px', padding: '0.4rem 0.5rem', fontSize: '0.85rem' }}
                  >
                    <BsExclamationTriangle size={13} />
                    Medium
                  </Button>
                  <Button
                    type="button"
                    variant={priority === 'HIGH' ? 'danger' : 'outline-secondary'}
                    size="sm"
                    className="flex-fill fw-bold d-flex align-items-center justify-content-center gap-1"
                    onClick={() => setPriority('HIGH')}
                    style={{ borderRadius: '6px', padding: '0.4rem 0.5rem', fontSize: '0.85rem' }}
                  >
                    <BsLightningFill size={13} />
                    High
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Dynamic Hierarchical Context Selection - Opens ONLY if Category is selected manually */}
            {!useFetchedContext && (
              <>
                {category === 'LAB' && (
                  <Card className="mb-3 bg-light border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <Card.Body className="p-3">
                      <div className="fw-semibold text-secondary small mb-2 text-uppercase" style={{ letterSpacing: '0.04em' }}>
                        Select Lab Context
                      </div>
                      <Form.Label className="small font-weight-bold text-muted">Select Lab</Form.Label>
                      <Form.Control
                        as="select"
                        size="sm"
                        value={labId}
                        onChange={(e) => setLabId(e.target.value)}
                        style={{ borderRadius: '6px' }}
                      >
                        {labs && labs.length > 0 ? (
                          labs.map((lab) => (
                            <option key={lab.id} value={lab.id}>
                              {lab.title}
                            </option>
                          ))
                        ) : (
                          <option value="">{loadingContexts ? 'Loading labs...' : 'No labs available for this course'}</option>
                        )}
                      </Form.Control>
                    </Card.Body>
                  </Card>
                )}

                {category === 'LAB_ACTIVITY' && (
                  <Card className="mb-3 bg-light border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <Card.Body className="p-3">
                      <div className="fw-semibold text-secondary small mb-2 text-uppercase" style={{ letterSpacing: '0.04em' }}>
                        Programming Activity Context
                      </div>
                      <Row className="g-2">
                        <Col md={6}>
                          <Form.Label className="small font-weight-bold text-muted">Step 1: Select Lab</Form.Label>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={labId}
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              setLabId(selectedId);
                              const labObj = labs.find(l => String(l.id) === String(selectedId));
                              const progActs = (labObj?.programming_activities && labObj.programming_activities.length > 0)
                                ? labObj.programming_activities
                                : (labObj?.activities || []).filter(a => a.type !== 'other');
                              if (progActs.length > 0) {
                                setActivityName(progActs[0].name || progActs[0].title);
                              } else {
                                setActivityName('');
                              }
                            }}
                            style={{ borderRadius: '6px' }}
                          >
                            {labs && labs.length > 0 ? (
                              labs.map((lab) => (
                                <option key={lab.id} value={lab.id}>
                                  {lab.title}
                                </option>
                              ))
                            ) : (
                              <option value="">{loadingContexts ? 'Loading labs...' : 'No labs available for this course'}</option>
                            )}
                          </Form.Control>
                        </Col>
                        <Col md={6}>
                          <Form.Label className="small font-weight-bold text-muted">Step 2: Select Programming Activity</Form.Label>
                          {(() => {
                            const currentLabObj = labs.find(l => String(l.id) === String(labId));
                            const progActs = (currentLabObj?.programming_activities && currentLabObj.programming_activities.length > 0)
                              ? currentLabObj.programming_activities
                              : (currentLabObj?.activities || []).filter(a => a.type !== 'other');
                            return (
                              <Form.Control
                                as="select"
                                size="sm"
                                value={activityName}
                                onChange={(e) => setActivityName(e.target.value)}
                                style={{ borderRadius: '6px' }}
                              >
                                {progActs.length > 0 ? (
                                  progActs.map((act, index) => (
                                    <option key={act.id || index} value={act.name || act.title}>
                                      {act.name || act.title}
                                    </option>
                                  ))
                                ) : (
                                  <option value="">No programming activities in this lab</option>
                                )}
                              </Form.Control>
                            );
                          })()}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {category === 'QUIZ_ACTIVITY' && (
                  <Card className="mb-3 bg-light border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <Card.Body className="p-3">
                      <div className="fw-semibold text-secondary small mb-2 text-uppercase" style={{ letterSpacing: '0.04em' }}>
                        Quiz Activity Hierarchy Context
                      </div>
                      <Row className="g-2">
                        <Col md={6}>
                          <Form.Label className="small font-weight-bold text-muted">Step 1: Select Lab</Form.Label>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={labId}
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              setLabId(selectedId);
                              const labObj = labs.find(l => String(l.id) === String(selectedId));
                              const quizActs = (labObj?.quiz_activities && labObj.quiz_activities.length > 0)
                                ? labObj.quiz_activities
                                : (labObj?.activities || []).filter(a => a.type === 'other' || (a.name && a.name.includes('(Quiz)')));
                              if (quizActs.length > 0) {
                                const cleanName = (quizActs[0].raw_name || quizActs[0].name || quizActs[0].title || '').replace(/\s*\(Quiz\)\s*$/i, '');
                                setActivityName(cleanName);
                              } else {
                                setActivityName('');
                              }
                            }}
                            style={{ borderRadius: '6px' }}
                          >
                            {labs && labs.length > 0 ? (
                              labs.map((lab) => (
                                <option key={lab.id} value={lab.id}>
                                  {lab.title}
                                </option>
                              ))
                            ) : (
                              <option value="">{loadingContexts ? 'Loading labs...' : 'No labs available for this course'}</option>
                            )}
                          </Form.Control>
                        </Col>
                        <Col md={6}>
                          <Form.Label className="small font-weight-bold text-muted">Step 2: Select Quiz Activity</Form.Label>
                          {(() => {
                            const currentLabObj = labs.find(l => String(l.id) === String(labId));
                            const quizActs = (currentLabObj?.quiz_activities && currentLabObj.quiz_activities.length > 0)
                              ? currentLabObj.quiz_activities
                              : (currentLabObj?.activities || []).filter(a => a.type === 'other' || (a.name && a.name.includes('(Quiz)')));
                            return (
                              <Form.Control
                                as="select"
                                size="sm"
                                value={activityName}
                                onChange={(e) => setActivityName(e.target.value)}
                                style={{ borderRadius: '6px' }}
                              >
                                {quizActs.length > 0 ? (
                                  quizActs.map((act, index) => {
                                    const cleanName = (act.raw_name || act.name || act.title || '').replace(/\s*\(Quiz\)\s*$/i, '');
                                    return (
                                      <option key={act.id || index} value={cleanName}>
                                        {cleanName}
                                      </option>
                                    );
                                  })
                                ) : (
                                  <option value="">No quiz activities found in this lab</option>
                                )}
                              </Form.Control>
                            );
                          })()}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {category === 'MULTIMEDIA_CONCEPT' && (
                  <Card className="mb-3 bg-light border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <Card.Body className="p-3">
                      <div className="fw-semibold text-secondary small mb-2 text-uppercase" style={{ letterSpacing: '0.04em' }}>
                        Multimedia Book Hierarchy Context
                      </div>
                      <Row className="g-2">
                        <Col md={6}>
                          <Form.Label className="small font-weight-bold text-muted">Step 1: Select Chapter</Form.Label>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={bookGroupId}
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              setBookGroupId(selectedId);
                              const groupObj = bookGroups.find(g => String(g.id) === String(selectedId));
                              if (groupObj && groupObj.concepts && groupObj.concepts.length > 0) {
                                setBookConceptTitle(groupObj.concepts[0].title || groupObj.concepts[0].name);
                              } else {
                                setBookConceptTitle('');
                              }
                            }}
                            style={{ borderRadius: '6px' }}
                          >
                            {bookGroups && bookGroups.length > 0 ? (
                              bookGroups.map((group) => (
                                <option key={group.id} value={group.id}>
                                  {group.title}
                                </option>
                              ))
                            ) : (
                              <option value="">{loadingContexts ? 'Loading chapters...' : 'No chapters available'}</option>
                            )}
                          </Form.Control>
                        </Col>
                        <Col md={6}>
                          <Form.Label className="small font-weight-bold text-muted">Step 2: Select Concept Topic</Form.Label>
                          {(() => {
                            const currentGroupObj = bookGroups.find(g => String(g.id) === String(bookGroupId));
                            const concepts = currentGroupObj ? currentGroupObj.concepts || [] : [];
                            return (
                              <Form.Control
                                as="select"
                                size="sm"
                                value={bookConceptTitle}
                                onChange={(e) => setBookConceptTitle(e.target.value)}
                                style={{ borderRadius: '6px' }}
                              >
                                {concepts.length > 0 ? (
                                  concepts.map((concept, index) => (
                                    <option key={concept.id || index} value={concept.title || concept.name}>
                                      {concept.title || concept.name}
                                    </option>
                                  ))
                                ) : (
                                  <option value="">General Chapter Query (No specific concept)</option>
                                )}
                              </Form.Control>
                            );
                          })()}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}
              </>
            )}

            {/* Title Input */}
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="mb-0 fw-semibold text-slate-700" style={{ fontSize: '0.92rem' }}>
                  Issue Title <span style={{ color: '#dc2626' }}>*</span>
                </Form.Label>
                <span className="text-muted small">{title.length} / 100</span>
              </div>
              <Form.Control
                type="text"
                placeholder="e.g. Testcase p1 output is wrong in Lab 6"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) setErrors({ ...errors, title: null });
                }}
                isInvalid={!!errors.title}
                style={{ borderRadius: '8px', fontSize: '0.92rem' }}
                maxLength={100}
              />
              <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
            </Form.Group>

            {/* Description Input */}
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="mb-0 fw-semibold text-slate-700" style={{ fontSize: '0.92rem' }}>
                  Description <span style={{ color: '#dc2626' }}>*</span>
                </Form.Label>
                <span className="text-muted small">{description.length} / 1000</span>
              </div>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="e.g. input values, expected output vs actual output..."
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) setErrors({ ...errors, description: null });
                }}
                isInvalid={!!errors.description}
                style={{ borderRadius: '8px', fontSize: '0.92rem', resize: 'vertical' }}
                maxLength={1000}
              />
              <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
            </Form.Group>

            {/* Drag and Drop Screenshot / Attachment Area */}
            <Form.Group className="mb-2">
              <Form.Label className="fw-semibold text-slate-700" style={{ fontSize: '0.92rem' }}>
                Attachment (Optional Screenshot / File)
              </Form.Label>

              {!mediaFile ? (
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    border: isDragOver ? '2px dashed #2563eb' : '2px dashed #cbd5e1',
                    background: isDragOver ? '#eff6ff' : '#f8fafc',
                    borderRadius: '10px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => document.getElementById('issueFileInput').click()}
                >
                  <MdCloudUpload size={36} className="text-secondary mb-2" />
                  <p className="mb-1 font-weight-bold text-dark" style={{ fontSize: '0.9rem' }}>
                    Click or drag & drop a screenshot / file here
                  </p>
                  <span className="text-muted small">Supports PNG, JPG, PDF, MP4 up to 10MB</span>
                  <input
                    id="issueFileInput"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept="image/*,.pdf,video/*"
                  />
                </div>
              ) : (
                <Card className="border-0 shadow-sm bg-light" style={{ borderRadius: '10px' }}>
                  <Card.Body className="p-3 d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="Attachment Preview"
                          style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                        />
                      ) : (
                        getFileIcon(mediaFile)
                      )}
                      <div>
                        <div className="fw-bold text-dark text-truncate" style={{ maxWidth: '280px', fontSize: '0.9rem' }}>
                          {mediaFile.name}
                        </div>
                        <div className="text-muted small">
                          {formatFileSize(mediaFile.size)} • <Badge bg="secondary">{(mediaFile.type && mediaFile.type.split('/')[1]) || 'file'}</Badge>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleRemoveFile}
                      className="d-flex align-items-center gap-1"
                      style={{ borderRadius: '6px' }}
                    >
                      <MdDelete size={16} />
                      Remove
                    </Button>
                  </Card.Body>
                </Card>
              )}

              {errors.mediaFile && (
                <div className="text-danger small mt-1 d-flex align-items-center gap-1">
                  <MdErrorOutline size={16} />
                  {errors.mediaFile}
                </div>
              )}
            </Form.Group>
          </Modal.Body>

          <Modal.Footer style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '0.85rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.65rem' }}>
            <Button
              variant="light"
              onClick={handleModalClose}
              style={{
                backgroundColor: '#ffffff',
                color: '#475569',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '0.88rem',
                padding: '0.45rem 1.25rem',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.15s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.color = '#1e293b';
                e.currentTarget.style.borderColor = '#94a3b8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.color = '#475569';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              style={{
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '0.88rem',
                padding: '0.45rem 1.35rem',
                backgroundColor: '#2563eb',
                borderColor: '#2563eb',
                boxShadow: '0 1px 2px rgba(37, 99, 235, 0.2)',
                transition: 'all 0.15s ease-in-out'
              }}
            >
              Submit Issue
            </Button>
          </Modal.Footer>
        </Form>
      ) : (
        /* Elegant Success Screen State with Actual Issue Data */
        createdIssueSummary && (
          <div>
            <Modal.Body style={{ padding: '1.75rem 2rem' }}>
              {/* Top Success Banner */}
              <div className="text-center mb-4">
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    backgroundColor: '#dcfce7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 0.75rem',
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.18)'
                  }}
                >
                  <MdCheckCircle size={34} style={{ color: '#16a34a' }} />
                </div>
                <h4 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '0.25rem' }}>
                  Issue Submitted Successfully!
                </h4>
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 0 }}>
                  Your issue <strong style={{ color: '#2563eb' }}>#{createdIssueSummary.id || 'New'}</strong> has been submitted successfully.
                </p>
              </div>

              {/* Actual Issue Details Card */}
              <Card
                className="border shadow-sm mb-3"
                style={{
                  borderRadius: '12px',
                  borderColor: '#e2e8f0',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'
                }}
              >
                {/* Header inside Card */}
                <div
                  style={{
                    padding: '0.75rem 1.25rem',
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span
                      style={{
                        backgroundColor: '#eff6ff',
                        color: '#1d4ed8',
                        fontWeight: '700',
                        fontSize: '0.85rem',
                        padding: '0.25rem 0.65rem',
                        borderRadius: '6px',
                        border: '1px solid #bfdbfe'
                      }}
                    >
                      Issue #{createdIssueSummary.id || 'New'}
                    </span>
                    <span
                      style={{
                        backgroundColor:
                          createdIssueSummary.priority === 'HIGH'
                            ? '#fef2f2'
                            : createdIssueSummary.priority === 'MEDIUM'
                            ? '#fffbeb'
                            : '#f0fdf4',
                        color:
                          createdIssueSummary.priority === 'HIGH'
                            ? '#b91c1c'
                            : createdIssueSummary.priority === 'MEDIUM'
                            ? '#b45309'
                            : '#15803d',
                        border: `1px solid ${
                          createdIssueSummary.priority === 'HIGH'
                            ? '#fecaca'
                            : createdIssueSummary.priority === 'MEDIUM'
                            ? '#fde68a'
                            : '#bbf7d0'
                        }`,
                        fontWeight: '600',
                        fontSize: '0.78rem',
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px'
                      }}
                    >
                      {createdIssueSummary.priority_display || createdIssueSummary.priority} Priority
                    </span>
                  </div>

                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      backgroundColor: '#e0f2fe',
                      color: '#0369a1',
                      fontSize: '0.78rem',
                      fontWeight: '600',
                      padding: '0.25rem 0.65rem',
                      borderRadius: '9999px',
                      border: '1px solid #bae6fd'
                    }}
                  >
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#0284c7' }}></span>
                    Status: {createdIssueSummary.status || 'Issue Unread'}
                  </span>
                </div>

                {/* Card Body with actual data */}
                <div style={{ padding: '1.25rem' }}>
                  {/* Title */}
                  <h5 style={{ fontWeight: '700', color: '#1e293b', marginBottom: '0.5rem', lineHeight: 1.4 }}>
                    {createdIssueSummary.title}
                  </h5>

                  {/* Context Breadcrumb */}
                  {createdIssueSummary.context && (
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        color: '#475569',
                        borderRadius: '6px',
                        padding: '0.35rem 0.75rem',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        marginBottom: '0.85rem',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <MdOpenInNew size={14} style={{ color: '#64748b' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {createdIssueSummary.context}
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  {createdIssueSummary.description && (
                    <div
                      style={{
                        backgroundColor: '#f8fafc',
                        border: '1px solid #f1f5f9',
                        borderRadius: '8px',
                        padding: '0.75rem 1rem',
                        fontSize: '0.88rem',
                        color: '#334155',
                        lineHeight: '1.5',
                        maxHeight: '120px',
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                        marginBottom: '0.75rem'
                      }}
                    >
                      {createdIssueSummary.description}
                    </div>
                  )}

                  {/* Attachment if present */}
                  {createdIssueSummary.mediaName && (
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        backgroundColor: '#f1f5f9',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '0.3rem 0.65rem',
                        fontSize: '0.8rem',
                        color: '#1e293b',
                        fontWeight: '500',
                        marginBottom: '0.75rem'
                      }}
                    >
                      <MdAttachFile size={15} style={{ color: '#2563eb' }} />
                      <span>{createdIssueSummary.mediaName}</span>
                    </div>
                  )}

                  {/* Timestamp */}
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                    Submitted on: {createdIssueSummary.created_at}
                  </div>
                </div>
              </Card>

              {/* Teaching Staff Reassurance Note */}
              <div
                style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '0.75rem 1rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.65rem'
                }}
              >
                <MdHelpOutline size={18} style={{ color: '#16a34a', marginTop: '2px', flexShrink: 0 }} />
                <div style={{ fontSize: '0.82rem', color: '#166534', lineHeight: 1.45 }}>
                  <strong>What happens next?</strong> Course teaching assistants and instructors will be notified.
                  You can track responses and reply to TA follow-ups under the <strong>Issues</strong> section in the course menu.
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer
              style={{
                background: '#f8fafc',
                borderTop: '1px solid #e2e8f0',
                padding: '0.85rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Button
                variant="light"
                onClick={handleModalClose}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontWeight: '600',
                  fontSize: '0.88rem',
                  padding: '0.45rem 1.25rem',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.15s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.borderColor = '#94a3b8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.color = '#475569';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  handleModalClose();
                  if (routeCourseId) {
                    const issuesPath = userMode === 'I'
                      ? `/courseware/course/${routeCourseId}/management/issues`
                      : `/courseware/course/${routeCourseId}/issues`;
                    navigate(issuesPath);
                  }
                }}
                style={{
                  borderRadius: '6px',
                  fontWeight: '600',
                  fontSize: '0.88rem',
                  padding: '0.45rem 1.25rem',
                  backgroundColor: '#2563eb',
                  borderColor: '#2563eb',
                  boxShadow: '0 1px 2px rgba(37, 99, 235, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  transition: 'all 0.15s ease-in-out'
                }}
              >
                View Issues Dashboard
                <MdOpenInNew size={15} />
              </Button>
            </Modal.Footer>
          </div>
        )
      )}
    </Modal>
  );
};

export default CreateIssueModal;
